<template lang="pug">
.message-list_item(@click="onClick")
  component(:class="{ 'message-disabled': shouldMessageDisabled }"
            :is="type"
            v-if="shouldDisplaySystemMessages"
            :message="message"
            :channel="channel"
            :inWebchat="inWebchat"
            :callJoined="callJoined"
            :systemMessagesDisplay="systemMessagesDisplay"
            :isTimestampAbsolute="isTimestampAbsolute"
            :accountFieldsVisibility="accountFieldsVisibility"
            @paginate="$emit('paginate', $event)"
            @search="$emit('search', $event)"
            @loaded="$emit('loaded', message)"
            @call="call"
            @signature="signature"
            @link="onLink"
            @reply="reply"
            @handleDropdownVisibleChange="handleDropdownVisibleChange")
    template(v-slot:message-cue v-if="shouldShowIcon('documents') || shouldShowIcon('assets')")
      .cue-container
        fa-icon.cue-icon(:icon="['fas', 'lightbulb-exclamation-on']")
    template(v-slot:message-buttons)
      aiq-dropdown-item.message-menu-item(v-if="shouldShowIcon('assets')", key="suggested-assets", command="suggested-assets")
        aiq-tooltip(effect="dark",
                    placement="top",
                    content="Suggested Assets",
                    :open-delay="500")
          .msg-icon(@click="onSuggestionClick('assets')")
            fa-icon(:icon="['fal', 'suitcase']" size="lg")
      aiq-dropdown-item.message-menu-item(v-if="shouldShowIcon('documents')", key="suggested-documents", command="suggested-documents")
        aiq-tooltip(effect="dark",
                    placement="top",
                    content="Suggested Documents",
                    :open-delay="500")
          .msg-icon(@click="onSuggestionClick('documents')")
            fa-icon(:icon="['fal', 'file-lines']" size="lg")
      aiq-dropdown-item.message-menu-item(v-if="canObfuscate && shouldShowIcon('obfuscation')", key="obfuscate", command="obfuscate")
        aiq-tooltip(effect="dark",
                    placement="top",
                    content="Mask Message",
                    :open-delay="500")
          .msg-icon(@click="onObfuscate()")
            fa-icon(:icon="['fal', 'eye-slash']")
      aiq-dropdown-item.message-menu-item(v-if="shouldShowIcon('translation.show')", key="translate", command="translate")
        aiq-tooltip(effect="dark",
                    placement="top",
                    content="Translate Message",
                    :open-delay="500")
          .msg-icon(@click="onTranslate()")
            i(:class="shouldShowIcon('translation.icon')")
      //- aiq-dropdown-item.message-menu-item(key="edit", command="edit")
      //-   i(class="el-icon-edit") --TODO: INCOMING FEATURE
      aiq-dropdown-item.message-menu-item(v-if="shouldShowIcon('delete')", key="delete", command="delete")
        aiq-tooltip(effect="dark",
                    placement="top",
                    content="Delete Message",
                    :open-delay="500")
          .msg-icon(@click="onDelete()")
            i(class="el-icon-delete")

  div.msg-legend(:class="{[senderType]: !inWebchat}")
    span {{ legend }}

  //- customer message sent status
  .message-status(:class="{'m-t-15': legend}", v-if="message.status")
    .status-message-sending(v-if="message.status.state === 'sending'")
      fa-icon.status-icon(:icon="['fas', 'circle-notch']" spin )
      .status-message {{ getStatusString(message) }}
    .status-message-sent(v-if="message.status.state === 'sent'")
      .status-icon.el-icon-check
      .status-message {{ getStatusString(message) }}
    .status-message-failure(v-if="message.status.state === 'failed'")
      .status-icon.el-icon-warning
      .status-message {{ getStatusString(message) }}
    .status-message-viewed(v-if="!inWebchat && message.status.state === 'viewed'")
      .status-message {{ getStatusString(message) }}

  //- agent message status
  .message-status(:class="{'m-t-15': legend}", v-if="message.payload.external_delivery_info")
    .status-message-failure(v-if="message.payload.external_delivery_info.delivery_status === 'failure'")
      .status-icon.el-icon-warning
      .status-message {{ message.payload.external_delivery_info.reason }}

</template>

<script>
import get from 'lodash/get';
import kebabCase from 'lodash/kebabCase';
import AssetMessage from './messageTypes/AssetMessage.vue';
import CardMessage from './messageTypes/CardMessage.vue';
import CarouselMessage from './messageTypes/CarouselMessage.vue';
import EmailMessage from './messageTypes/Email.vue';
import FileMessage from './messageTypes/FileMessage.vue';
import LegacyMessage from './messageTypes/LegacyMessage.vue';
import RegularMessage from './messageTypes/RegularMessage.vue';
import RepliesMessage from './messageTypes/RepliesMessage.vue';
import RichTextMessage from './messageTypes/RichTextMessage.vue';
import EventMessage from './messageTypes/EventMessage.vue';
import SystemMessage from './messageTypes/SystemMessage.vue';
import TwitterDmMessage from './messageTypes/TwitterDM.vue';
import TwitterMessage from './messageTypes/Twitter.vue';
import UrlMessage from './messageTypes/UrlMessage.vue';
import CallMessage from './messageTypes/CallMessage.vue';
import SignatureMessage from './messageTypes/SignatureMessage.vue';
import i18n from '../../mixins/i18n';

export default {
  name: 'message',
  mixins: [i18n],
  components: {
    AssetMessage,
    CardMessage,
    CarouselMessage,
    EmailMessage,
    FileMessage,
    LegacyMessage,
    RegularMessage,
    RepliesMessage,
    RichTextMessage,
    EventMessage,
    SystemMessage,
    TwitterDmMessage,
    TwitterMessage,
    UrlMessage,
    CallMessage,
    SignatureMessage,
  },
  props: {
    message: {
      type: Object,
    },
    channel: {
      type: String,
      default: () => '',
    },
    callJoined: {
      type: Boolean,
      default: () => false,
    },
    showIcons: {
      type: Object,
      default: () => ({
        assets: false,
        documents: false,
        obfuscation: true,
        translation: {},
      }),
    },

    // (Gabe) For now put these un-namespaced props at the root message level
    replyitem: Object,
    disabled: Boolean,
    inWebchat: {
      type: Boolean,
      default: false,
    },
    systemMessagesDisplay: {
      type: Boolean,
      default: true,
    },
    isTimestampAbsolute: {
      type: Boolean,
      default: false,
    },
    accountFieldsVisibility: {
      type: Array,
      default: () => [],
    },
  },
  emits: [
    'paginate',
    'search',
    'loaded',
    'suggestionsClick',
    'obfuscate',
    'translate',
    'delete',
    'reply',
    'call',
    'signature',
    'click',
    'focusOnInputbox',
    'link',
  ],
  computed: {
    senderType() {
      return get(this.message, 'sender_type', '');
    },
    shouldMessageDisabled() {
      return get(this.message, 'payload.external_delivery_info.delivery_status', 'success') === 'failure';
    },
    legend() {
      const type = get(this.message, 'payload.message_type', '');
      const path = type ? `payload.${type}` : 'payload';
      const { from } = get(this.message, `${path}.translation`, {});
      if (from) {
        const language = this.getString(`language.${from}`);
        return this.getString('messages.message_legend', { language, defaultValue: 'Translated from {{language}}' });
      }
      return get(this.message, `${path}.legend`); // Backwards compatibility
    },
    type() {
      const type = get(this.message, 'payload.message_type', 'legacy');
      return kebabCase(`${type}-message`);
    },
    shouldDisplaySystemMessages() {
      const rv = this.systemMessagesDisplay
        || !['event-message', 'system-message'].includes(this.type);
      return rv;
    },
    canObfuscate() {
      const ret = !get(this.message, 'obfuscated', 'true')
        && this.senderType === 'customers'
        && !this.inWebchat;
      return ret;
    },
  },
  data() {
    return {};
  },
  methods: {
    shouldShowIcon(name) {
      return get(this.showIcons, name, false);
    },
    onSuggestionClick(type) {
      const payload = {
        type,
        msgId: this.message.id,
      };

      this.$emit('suggestionsClick', payload);
    },
    onObfuscate() {
      this.$emit('obfuscate', this.message);
    },
    onTranslate() {
      this.$emit('translate', this.message);
    },
    onDelete() {
      this.$emit('delete', this.message);
    },
    reply(selectedItem) {
      this.$emit('reply', selectedItem);
    },
    call(msg) {
      this.$emit('call', msg);
    },
    signature(msg) {
      this.$emit('signature', msg);
    },
    onLink(msg) {
      this.$emit('link', msg);
    },
    onClick() {
      // Enable clickability for resending failed message.
      this.$emit('click', this.message);
    },
    getStatusString(m) {
      return this.getString(`messages.status.${m.status.state}`, m.status.message);
    },
    handleDropdownVisibleChange(isVisible) {
      if (!isVisible) {
        this.$emit('focusOnInputbox');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../aiq-theme/aiq-variables';

.message-status {
  margin-top: 1px;
}

.m-t-15 {
  margin-top: 15px;
}

.status-message-sending, .status-message-sent, .status-message-failure {
  display: flex;
  justify-content: flex-end;
  padding-right: 15px;
  font-size: 10px;
  align-content: space-between;
}

.status-message-sending {
  padding-top: 5px;
}

.status-message-viewed {
  display: flex;
  justify-content: flex-end;
  padding-right: 40px;
  font-size: 12px;
  color: #99A9BF;
  align-content: space-between;
}


.status-icon {
  margin-right: 5px;
}

.status-message-failure {
  color: red;
}

.msg-legend {
  margin-left: 40px;
  span {
    color: #99A9BF;
    font-size: 12px;
    line-height: 12px;
    font-weight: 300;
    font-style: italic;
  }
}

.cue-container {
  position: absolute;
  top: -4px;
  right: -8px;

  .cue-icon {
    color: $aiq-background-message-item-selected;
  }
}

.message-disabled {
  opacity: .5;
}
.status-message-failure {
  display: flex;
  align-items: center;
}
</style>
